/**
 * Hook for payment processing methods
 */
import { useCallback } from 'react';
import type {
    CardPaymentMethod,
    ApplePayToken,
    GooglePayToken,
    PaymentOptions,
    PaymentResponse,
    PaymentInstrumentResponse,
} from '../../../core/resources/payments';
import type { PaymentsResource } from '../../../core/resources/payments';
import type { UsePaymentOptions } from '../usePaymentQuery';
import type { PaymentInstrument } from '../useThreeds';
import { getAssignedPaymentFlowId } from '../../../core/funnelClient';

interface UsePaymentProcessorsParams {
    paymentsResource: PaymentsResource;
    createCardPaymentInstrument: (cardData: CardPaymentMethod) => Promise<PaymentInstrumentResponse>;
    createApplePayPaymentInstrument: (applePayToken: ApplePayToken) => Promise<PaymentInstrumentResponse>;
    createGooglePayPaymentInstrument: (googlePayToken: GooglePayToken) => Promise<PaymentInstrumentResponse>;
    createSession: any;
    handlePaymentAction: any;
    startPolling: any;
    setIsLoading: (loading: boolean) => void;
    setError: (error: string | null) => void;
    setCurrentPaymentId: (id: string | null) => void;
    hookOptionsRef: React.MutableRefObject<UsePaymentOptions | undefined>;
    storeConfig: any;
}

export function usePaymentProcessors({
    paymentsResource,
    createCardPaymentInstrument,
    createApplePayPaymentInstrument,
    createGooglePayPaymentInstrument,
    createSession,
    handlePaymentAction,
    startPolling,
    setIsLoading,
    setError,
    setCurrentPaymentId,
    hookOptionsRef,
    storeConfig,
}: UsePaymentProcessorsParams) {
    // Process payment directly with checkout session
    const processPaymentDirect = useCallback(
        async (
            checkoutSessionId: string,
            paymentInstrumentId: string,
            threedsSessionId?: string,
            options: PaymentOptions = {},
        ): Promise<PaymentResponse> => {
            try {
                const paymentFlowId = options.paymentFlowId || getAssignedPaymentFlowId();

                const response = await paymentsResource.processPaymentDirect(
                    checkoutSessionId,
                    paymentInstrumentId,
                    threedsSessionId,
                    {
                        initiatedBy: options.initiatedBy,
                        source: options.source,
                        paymentFlowId,
                        processorId: options.processorId,
                        paymentMethod: options.paymentMethod,
                        shippingRateId: options.shippingRateId,
                    }
                );

                setCurrentPaymentId(response.payment?.id);

                if (response.payment.requireAction !== 'none') {
                    await handlePaymentAction(response.payment, options);
                } else if (response.payment.status === 'succeeded') {
                    setIsLoading(false);
                    const successResponse = {
                        ...response,
                        order: response.order || (response.payment as any).order,
                    };

                    if (hookOptionsRef.current?.onPaymentCompleted) {
                        await hookOptionsRef.current.onPaymentCompleted(response.payment, {
                            isRedirectReturn: false,
                            order: successResponse.order,
                            checkoutSessionId,
                        });
                    }

                    options.onSuccess?.(successResponse);
                    options.onPaymentSuccess?.(successResponse);
                } else {
                    startPolling(response.payment?.id, {
                        onRequireAction: (payment: any) => {
                            void handlePaymentAction(payment, options);
                        },
                        onSuccess: async (payment: any) => {
                            setIsLoading(false);
                            const successResponse = {
                                paymentId: payment.id,
                                payment,
                                order: (payment as any).order,
                            };

                            if (hookOptionsRef.current?.onPaymentCompleted) {
                                await hookOptionsRef.current.onPaymentCompleted(payment, {
                                    isRedirectReturn: false,
                                    order: successResponse.order,
                                    checkoutSessionId,
                                });
                            }

                            options.onSuccess?.(successResponse);
                            options.onPaymentSuccess?.(successResponse);
                        },
                        onFailure: async (errorMsg: string) => {
                            setError(errorMsg);
                            setIsLoading(false);

                            if (hookOptionsRef.current?.onPaymentFailed) {
                                await hookOptionsRef.current.onPaymentFailed(errorMsg, {
                                    isRedirectReturn: false,
                                });
                            }

                            options.onFailure?.(errorMsg);
                            options.onPaymentFailed?.({
                                code: 'PAYMENT_FAILED',
                                message: errorMsg,
                                payment: response.payment,
                            });
                        },
                    });
                }

                return response;
            } catch (_error) {
                const errorMsg = _error instanceof Error ? _error.message : 'Payment failed';
                setError(errorMsg);
                setIsLoading(false);
                options.onFailure?.(errorMsg);
                options.onPaymentFailed?.({
                    code: 'PAYMENT_PROCESSING_ERROR',
                    message: errorMsg,
                });
                throw _error;
            }
        },
        [paymentsResource, handlePaymentAction, startPolling, setIsLoading, setError, setCurrentPaymentId, hookOptionsRef],
    );

    // Process APM payment (Alternative Payment Methods)
    const processApmPayment = useCallback(
        async (
            checkoutSessionId: string,
            options: {
                processorId: string;
                paymentMethod: string;
                initiatedBy?: 'customer' | 'merchant';
                source?: 'upsell' | 'checkout' | 'offer' | 'missing_club' | 'forced';
                paymentFlowId?: string;
            } & Pick<PaymentOptions, 'onFailure' | 'onPaymentFailed' | 'onSuccess' | 'onPaymentSuccess'>,
        ): Promise<PaymentResponse> => {
            setIsLoading(true);
            setError(null);

            try {
                console.log('[processApmPayment] Processing APM payment directly:', options);

                // For APM payments, pass empty paymentInstrumentId and let backend handle it with processorId/paymentMethod
                return await processPaymentDirect(checkoutSessionId, '', undefined, options);
            } catch (_error) {
                setIsLoading(false);
                const errorMsg = _error instanceof Error ? _error.message : 'APM payment failed';
                setError(errorMsg);
                options.onFailure?.(errorMsg);
                options.onPaymentFailed?.({
                    code: 'APM_PAYMENT_ERROR',
                    message: errorMsg,
                });
                throw _error;
            }
        },
        [processPaymentDirect, setIsLoading, setError],
    );

    // Process card payment
    const processCardPayment = useCallback(
        async (
            checkoutSessionId: string,
            cardData: CardPaymentMethod,
            options: PaymentOptions = {},
        ): Promise<PaymentResponse> => {
            setIsLoading(true);
            setError(null);

            try {
                // Create payment instrument and handle 3DS for card payments
                const paymentInstrument = await createCardPaymentInstrument(cardData);
                const shouldCreateThreedsSession = storeConfig?.computed?.threedsEnabled;
                let threedsSessionId: string | undefined;
                if (shouldCreateThreedsSession) {
                    try {
                        const threedsSession = await createSession(paymentInstrument as PaymentInstrument, {
                            provider: options.threedsProvider || 'basis_theory',
                        });
                        threedsSessionId = threedsSession.id;
                    } catch (_error) {
                        // Continue without 3DS session
                    }
                }

                return await processPaymentDirect(checkoutSessionId, paymentInstrument.id, threedsSessionId, options);
            } catch (_error) {
                setIsLoading(false);
                const errorMsg = _error instanceof Error ? _error.message : 'Payment failed';
                setError(errorMsg);
                options.onFailure?.(errorMsg);
                options.onPaymentFailed?.({
                    code: 'CARD_PAYMENT_ERROR',
                    message: errorMsg,
                });
                throw _error;
            }
        },
        [createCardPaymentInstrument, createSession, processPaymentDirect, storeConfig, setIsLoading, setError],
    );

    // Process Apple Pay payment
    const processApplePayPayment = useCallback(
        async (
            checkoutSessionId: string,
            applePayToken: ApplePayToken,
            options: PaymentOptions = {},
        ): Promise<PaymentResponse> => {
            setIsLoading(true);
            setError(null);

            try {
                const paymentInstrument = await createApplePayPaymentInstrument(applePayToken);
                return await processPaymentDirect(checkoutSessionId, paymentInstrument.id, undefined, options);
            } catch (_error) {
                setIsLoading(false);
                const errorMsg = _error instanceof Error ? _error.message : 'Apple Pay payment failed';
                setError(errorMsg);
                options.onFailure?.(errorMsg);
                options.onPaymentFailed?.({
                    code: 'APPLE_PAY_ERROR',
                    message: errorMsg,
                });
                throw _error;
            }
        },
        [createApplePayPaymentInstrument, processPaymentDirect, setIsLoading, setError],
    );

    // Process Google Pay payment
    const processGooglePayPayment = useCallback(
        async (
            checkoutSessionId: string,
            googlePayToken: GooglePayToken,
            options: PaymentOptions = {},
        ): Promise<PaymentResponse> => {
            setIsLoading(true);
            setError(null);

            try {
                const paymentInstrument = await createGooglePayPaymentInstrument(googlePayToken);
                return await processPaymentDirect(checkoutSessionId, paymentInstrument.id, undefined, options);
            } catch (_error) {
                setIsLoading(false);
                const errorMsg = _error instanceof Error ? _error.message : 'Google Pay payment failed';
                setError(errorMsg);
                options.onFailure?.(errorMsg);
                options.onPaymentFailed?.({
                    code: 'GOOGLE_PAY_ERROR',
                    message: errorMsg,
                });
                throw _error;
            }
        },
        [createGooglePayPaymentInstrument, processPaymentDirect, setIsLoading, setError],
    );

    // Process payment with existing instrument
    const processPaymentWithInstrument = useCallback(
        async (
            checkoutSessionId: string,
            paymentInstrumentId: string,
            options: PaymentOptions = {},
        ): Promise<PaymentResponse> => {
            setIsLoading(true);
            setError(null);

            try {
                return await processPaymentDirect(checkoutSessionId, paymentInstrumentId, undefined, options);
            } catch (_error) {
                setIsLoading(false);
                const errorMsg = _error instanceof Error ? _error.message : 'Payment failed';
                setError(errorMsg);
                options.onFailure?.(errorMsg);
                options.onPaymentFailed?.({
                    code: 'PAYMENT_INSTRUMENT_ERROR',
                    message: errorMsg,
                });
                throw _error;
            }
        },
        [processPaymentDirect, setIsLoading, setError],
    );

    return {
        processCardPayment,
        processApplePayPayment,
        processGooglePayPayment,
        processPaymentWithInstrument,
        processApmPayment,
    };
}
